<script setup lang="ts">
import type { Editor } from '@tiptap/vue-3'

const props = defineProps<{
  // TODO: There is some typescript weirdness here. The UEditor scoped slot editor is slightly different from the @tiptap/vue-3 Editor type
  editor: Editor | any
  autoOpen?: boolean
}>()

const open = ref(false)
const url = ref('')

const active = computed(() => props.editor.isActive('link'))
const disabled = computed(() => {
  if (!props.editor.isEditable) return true
  const { selection } = props.editor.state
  return selection.empty && !props.editor.isActive('link')
})

watch(
  () => props.editor,
  (editor, _, onCleanup) => {
    if (!editor) return

    const updateUrl = () => {
      const { href } = editor.getAttributes('link')
      url.value = href || ''
    }

    updateUrl()
    editor.on('selectionUpdate', updateUrl)

    onCleanup(() => {
      editor.off('selectionUpdate', updateUrl)
    })
  },
  { immediate: true },
)

watch(active, (isActive) => {
  if (isActive && props.autoOpen) {
    open.value = true
  }
})

function setLink() {
  if (!url.value) return

  const { selection } = props.editor.state
  const isEmpty = selection.empty
  const hasCode = props.editor.isActive('code')

  let chain = props.editor.chain().focus()

  // When linking code, extend the code mark range first to select the full code
  if (hasCode && !isEmpty) {
    chain = chain.extendMarkRange('code').setLink({ href: url.value })
  } else {
    chain = chain.extendMarkRange('link').setLink({ href: url.value })

    if (isEmpty) {
      chain = chain.insertContent({ type: 'text', text: url.value })
    }
  }

  chain.run()
  open.value = false
}

function removeLink() {
  props.editor
    .chain()
    .focus()
    .extendMarkRange('link')
    .unsetLink()
    .setMeta('preventAutolink', true)
    .run()

  url.value = ''
  open.value = false
}

function openLink() {
  if (!url.value) return
  window.open(url.value, '_blank', 'noopener,noreferrer')
}

function handleKeyDown(event: KeyboardEvent) {
  if (event.key === 'Enter') {
    event.preventDefault()
    setLink()
  }
}
</script>

<template>
  <UPopover v-model:open="open" :ui="{ content: 'p-0.5' }">
    <UTooltip text="Link">
      <UButton
        :active="active"
        :disabled="disabled"
        active-color="primary"
        active-variant="soft"
        color="neutral"
        icon="i-lucide-link"
        size="sm"
        variant="ghost"
      />
    </UTooltip>

    <template #content>
      <UInput
        v-model="url"
        name="url"
        autofocus
        placeholder="Paste a link..."
        type="url"
        variant="none"
        @keydown="handleKeyDown"
      >
        <div class="mr-0.5 flex items-center">
          <UButton
            :disabled="!url && !active"
            icon="i-lucide-corner-down-left"
            size="sm"
            title="Apply link"
            variant="ghost"
            @click="setLink"
          />

          <USeparator class="mx-1 h-6" orientation="vertical" />

          <UButton
            :disabled="!url && !active"
            color="neutral"
            icon="i-lucide-external-link"
            size="sm"
            title="Open in new window"
            variant="ghost"
            @click="openLink"
          />

          <UButton
            :disabled="!url && !active"
            color="neutral"
            icon="i-lucide-trash"
            size="sm"
            title="Remove link"
            variant="ghost"
            @click="removeLink"
          />
        </div>
      </UInput>
    </template>
  </UPopover>
</template>
